import { useState, type MouseEvent } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { AppBar, Toolbar, Box, Link as MuiLink, Tooltip, IconButton } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import type { SvgIconComponent } from "@mui/icons-material";
import AccountMenu from "@/shared/ui/appHeader/AccountMenu";
import HeaderSearchField from "@/shared/ui/appHeader/HeaderSearchField";
import { useCurrentUser } from "@/domains/profile/hooks/useCurrentUser";
import { useFirm } from "@/domains/firms/hooks/useFirm";
import { usePostHogReset } from "@/providers/PostHogRoot";
import { useAuth } from "@/providers/useAuth";
import { useThemeContext } from "@/providers/ThemeContext";
import { agentConfig } from "@/shared/config/agentConfig";
import { isDemoMode, setDemoMode } from "@/shared/config/demoMode";
import { MOBILE_BOTTOM_NAV_HEIGHT_PX } from "@/shared/ui/appHeader/mobileBottomNav";
import { HEADER_COMPACT_BREAKPOINT_PX, useHeaderCompact } from "@/shared/ui/useCompactLayout";
import {
  APP_HEADER_HEIGHT_PX,
  chromeLeadingHide,
  chromeLeadingOpacity,
  chromeRemaining,
} from "@/shared/ui/compactChrome/chromeCss";
import { useChromeHidden } from "@/shared/ui/compactChrome/useCompactChrome";
import { useDockedAsideOffset } from "@/shared/ui/dockedAsideContext";

const NAV_ITEM_HEIGHT_PX = 36;
const NAV_ITEM_ICON_SIZE_PX = 15;
const NAV_LABEL_FONT_SIZE_PX = 11.375;
const NAV_LABEL_LINE_HEIGHT_PX = 20;
// Design: 8px vertical padding around the 20px label row inside 36px tabs.
const NAV_ITEM_V_PAD_PX = (NAV_ITEM_HEIGHT_PX - NAV_LABEL_LINE_HEIGHT_PX) / 2;

const NAV_LINK_SX = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  boxSizing: "border-box",
  py: `${String(NAV_ITEM_V_PAD_PX)}px`,
  px: 2,
  minHeight: NAV_ITEM_HEIGHT_PX,
  height: NAV_ITEM_HEIGHT_PX,
  borderRadius: "8px",
  fontFamily: "var(--font-sans)",
  fontSize: `${NAV_LABEL_FONT_SIZE_PX}px`,
  fontWeight: 500,
  lineHeight: `${NAV_LABEL_LINE_HEIGHT_PX}px`,
  textDecoration: "none",
  color: "inherit",
  cursor: "pointer",
} as const;

const NAV_ICON_SX = {
  fontSize: NAV_ITEM_ICON_SIZE_PX,
  lineHeight: 0,
  display: "block",
  flexShrink: 0,
} as const;

const NAV_LABEL_SX = {
  display: "block",
  lineHeight: `${NAV_LABEL_LINE_HEIGHT_PX}px`,
} as const;

function getNavItemStateSx(disabled: boolean, isActive: boolean) {
  if (disabled) {
    return { color: "rgba(255, 255, 255, 0.35)", cursor: "default" };
  }
  if (isActive) {
    return {
      bgcolor: "#1D4ED8",
      color: "#ffffff",
      "&:hover": { bgcolor: "#1D4ED8" },
    };
  }
  return {
    color: "#D1D5DB",
    "&:hover": { bgcolor: "rgba(255, 255, 255, 0.12)" },
  };
}

function NavItem({
  Icon,
  label,
  to,
  isActive = false,
  disabled = false,
  singleLineOnLarge = false,
}: Readonly<{
  Icon: SvgIconComponent;
  label: string;
  to?: string;
  isActive?: boolean;
  disabled?: boolean;
  /** Keep the label on one line from the `lg` breakpoint up. Smaller screens stay unchanged. */
  singleLineOnLarge?: boolean;
}>) {
  const stateSx = getNavItemStateSx(disabled, isActive);
  // `lg` and up only. Below that the label keeps the default wrap.
  const singleLineSx = singleLineOnLarge
    ? { flexShrink: { lg: 0 }, whiteSpace: { lg: "nowrap" } }
    : {};

  const content = (
    <>
      <Icon sx={NAV_ICON_SX} />
      <Box
        component="span"
        sx={singleLineOnLarge ? { ...NAV_LABEL_SX, whiteSpace: { lg: "nowrap" } } : NAV_LABEL_SX}
      >
        {label}
      </Box>
    </>
  );

  if (disabled || !to) {
    return (
      <Box component="span" sx={{ ...NAV_LINK_SX, ...stateSx, ...singleLineSx }}>
        {content}
      </Box>
    );
  }

  return (
    <Box
      component={Link}
      to={to}
      search={clearSearch}
      sx={{ ...NAV_LINK_SX, ...stateSx, ...singleLineSx }}
    >
      {content}
    </Box>
  );
}

const NAV_LINKS: ReadonlyArray<{
  label: string;
  to: string;
  Icon: SvgIconComponent;
  singleLineOnLarge?: boolean;
}> = [
  // Tower AI only appears when the agent feature flag is on, matching the drawer
  // launcher: with the flag off the page has nothing but its unavailable state.
  // Two words: on large screens the nav may shrink, so keep this label on one line.
  ...(agentConfig.enabled
    ? [{ label: "Tower AI", to: "/ai-assist", Icon: AutoAwesomeIcon, singleLineOnLarge: true }]
    : []),
  { label: "Munis", to: "/markets", Icon: ShowChartIcon },
  { label: "Watchlists", to: "/watchlists", Icon: PlaylistAddIcon },
];

const DISABLED_NAV_LINKS: ReadonlyArray<{ label: string; Icon: SvgIconComponent }> = [
  { label: "Portfolios", Icon: AccountBalanceIcon },
  { label: "WatchTower", Icon: VisibilityIcon },
];

function BottomNavItem({
  Icon,
  label,
  to,
  isActive = false,
  disabled = false,
}: Readonly<{
  Icon: SvgIconComponent;
  label: string;
  to?: string;
  isActive?: boolean;
  disabled?: boolean;
}>) {
  const stateSx = getNavItemStateSx(disabled, isActive);
  const content = (
    <>
      <Icon sx={{ fontSize: 20, lineHeight: 0, display: "block", flexShrink: 0 }} />
      <Box
        component="span"
        sx={{
          display: "block",
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: "10px",
          lineHeight: "12px",
        }}
      >
        {label}
      </Box>
    </>
  );
  const itemSx = {
    flex: "1 1 0",
    minWidth: 0,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "2px",
    mx: "2px",
    my: "6px",
    px: "4px",
    borderRadius: "8px",
    textDecoration: "none",
    fontFamily: "var(--font-sans)",
    fontWeight: 500,
    ...stateSx,
  } as const;

  if (disabled || !to) {
    return (
      <Box component="span" sx={itemSx}>
        {content}
      </Box>
    );
  }

  return (
    <Box component={Link} to={to} search={clearSearch} sx={itemSx}>
      {content}
    </Box>
  );
}

function MobileBottomNav({
  pathname,
  accountOpen,
  onAccountClick,
}: Readonly<{
  pathname: string;
  accountOpen: boolean;
  onAccountClick: (event: MouseEvent<HTMLElement>) => void;
}>) {
  const hidden = useChromeHidden(true);
  // The docked assistant pushes the shell left with a margin, but this fixed bar
  // is out of flow — shift it by the same width so the drawer does not cover it.
  const dockedOffset = useDockedAsideOffset();
  return (
    <Box
      component="nav"
      aria-label="Primary"
      inert={hidden ? true : undefined}
      sx={(theme) => ({
        pointerEvents: hidden ? "none" : undefined,
        position: "fixed",
        left: 0,
        right: `${dockedOffset}px`,
        bottom: 0,
        zIndex: theme.zIndex.appBar,
        display: "flex",
        alignItems: "stretch",
        boxSizing: "border-box",
        height: `calc(${MOBILE_BOTTOM_NAV_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))`,
        pb: "env(safe-area-inset-bottom, 0px)",
        pl: "env(safe-area-inset-left, 0px)",
        pr: "env(safe-area-inset-right, 0px)",
        backgroundColor: theme.app?.headerBackground ?? "#0F172A",
        borderTop: "1px solid #334155",
        transform: `translate3d(0, ${chromeLeadingHide(MOBILE_BOTTOM_NAV_HEIGHT_PX)}, 0)`,
        opacity: chromeLeadingOpacity(MOBILE_BOTTOM_NAV_HEIGHT_PX),
        transition: theme.transitions.create("right", {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        willChange: "transform, opacity, right",
      })}
    >
      {NAV_LINKS.map((item) => (
        <BottomNavItem
          key={item.label}
          Icon={item.Icon}
          label={item.label}
          to={item.to}
          isActive={pathname.startsWith(item.to)}
        />
      ))}
      {DISABLED_NAV_LINKS.map((item) => (
        <BottomNavItem key={item.label} Icon={item.Icon} label={item.label} disabled />
      ))}
      <Box
        component="button"
        type="button"
        aria-label="Account settings"
        aria-haspopup="true"
        aria-expanded={accountOpen ? "true" : undefined}
        aria-controls={accountOpen ? "account-menu" : undefined}
        onClick={onAccountClick}
        sx={{
          flex: "1 1 0",
          minWidth: 0,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "2px",
          mx: "2px",
          my: "6px",
          px: "4px",
          border: 0,
          borderRadius: "8px",
          background: "transparent",
          color: "#D1D5DB",
          fontFamily: "var(--font-sans)",
          fontWeight: 500,
          fontSize: "10px",
          lineHeight: "12px",
          cursor: "pointer",
          "&:hover": { bgcolor: "rgba(255, 255, 255, 0.12)" },
        }}
      >
        <PersonOutlineOutlinedIcon sx={{ fontSize: 20, lineHeight: 0, display: "block" }} />
        <Box component="span" sx={{ fontSize: "10px", lineHeight: "12px" }}>
          Account
        </Box>
      </Box>
    </Box>
  );
}

function getProfileLabel(name: string | null | undefined): string {
  return name?.trim() || "Profile";
}

function runAsync(task: unknown): void {
  Promise.resolve(task).catch(() => {
    // Header actions are best-effort and should not break UI interaction.
  });
}

// Drop all search params when navigating between top-level pages so e.g. the
// Watchlists `?watchlist=...` selection never leaks onto Markets (and vice
// versa). TanStack Router preserves the current search by default; returning a
// fresh object clears it. The `as never` satisfies the reducer signature the
// router infers for a generic string `to` (where the search schema is unknown).
const clearSearch = (): never => ({}) as never;

/** Markets Messages has its own list. The header security search does not apply there. */
function isMarketsMessagesTab(pathname: string, searchStr: string): boolean {
  if (pathname !== "/markets") return false;
  return new URLSearchParams(searchStr).get("tab") === "messages";
}

function HeaderBrand({
  compact,
  firmName,
}: Readonly<{ compact: boolean; firmName: string | undefined }>) {
  return (
    <MuiLink
      component={Link}
      to="/markets"
      search={clearSearch}
      color="inherit"
      underline="none"
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: "12px",
        pr: "16px",
        flexShrink: compact ? 1 : 0,
        minWidth: compact ? 0 : undefined,
        [theme.breakpoints.between(HEADER_COMPACT_BREAKPOINT_PX, "lg")]: {
          flexShrink: 1,
          minWidth: 0,
        },
      })}
    >
      <Box
        sx={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 40,
          height: 40,
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src="/assets/logo.png"
          alt="Tower"
          sx={{ display: "block", objectFit: "contain", width: 32, height: 32 }}
        />
      </Box>
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          width: compact ? "auto" : 122.8,
          maxWidth: compact ? 122.8 : undefined,
          minWidth: 0,
          flexShrink: compact ? 1 : 0,
          overflow: "hidden",
          [theme.breakpoints.between(HEADER_COMPACT_BREAKPOINT_PX, "lg")]: {
            width: "auto",
            maxWidth: 122.8,
            flexShrink: 1,
          },
        })}
      >
        <Box
          component="span"
          title={firmName}
          sx={{
            fontFamily: "var(--font-sans)",
            fontWeight: 700,
            fontSize: "11.375px",
            lineHeight: "14px",
            color: "#ffffff",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {firmName ?? "\u00A0"}
        </Box>
        <Box
          component="span"
          sx={{
            fontFamily: "var(--font-sans)",
            fontWeight: 400,
            fontSize: "9.75px",
            lineHeight: "12px",
            color: "#60A5FA",
            mt: "4px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          TowerBonds Platform
        </Box>
      </Box>
    </MuiLink>
  );
}

function HeaderActions({
  compact,
  hideSearch,
  menuOpen,
  onAvatarClick,
}: Readonly<{
  compact: boolean;
  hideSearch: boolean;
  menuOpen: boolean;
  onAvatarClick: (event: MouseEvent<HTMLElement>) => void;
}>) {
  return (
    <>
      {compact ? null : (
        <Box
          sx={(theme) => ({
            flex: 1,
            minWidth: 16,
            // The search cluster takes the leftover width in this band.
            [theme.breakpoints.down("lg")]: {
              flex: "0 0 0px",
              minWidth: 0,
              width: 0,
            },
          })}
        />
      )}
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          gap: compact ? "8px" : "16px",
          flexShrink: compact ? 1 : 0,
          flex: compact ? "1 1 auto" : undefined,
          minWidth: compact ? 0 : undefined,
          [theme.breakpoints.between(HEADER_COMPACT_BREAKPOINT_PX, "lg")]: {
            flex: "1 1 auto",
            flexShrink: 1,
            // Keep the resting search at its 200px floor instead of squeezing it away.
            minWidth: "auto",
            justifyContent: "flex-end",
          },
        })}
      >
        {hideSearch ? null : <HeaderSearchField />}

        {compact ? null : (
          <Tooltip title="Account settings">
            <IconButton
              onClick={onAvatarClick}
              aria-controls={menuOpen ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={menuOpen ? "true" : undefined}
              sx={{
                boxSizing: "border-box",
                width: 36,
                height: 36,
                p: "4px",
                borderRadius: "6px",
                bgcolor: "#334155",
                "&:hover": { bgcolor: "#475569" },
              }}
            >
              <PersonOutlineOutlinedIcon
                sx={{
                  boxSizing: "border-box",
                  display: "block",
                  width: 16,
                  height: 16,
                  fontSize: 16,
                  color: "#D1D5DB",
                }}
              />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </>
  );
}

export default function AppHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const compact = useHeaderCompact();
  const headerHidden = useChromeHidden(compact);
  // Keep the fixed compact bar aligned with the shell when the assistant docks.
  const dockedOffset = useDockedAsideOffset();
  const { data: currentUser } = useCurrentUser();
  const { data: firm } = useFirm(currentUser?.firmId);
  // useAuth returns a stable, bound signOut; `this` is never rebound.
  // oxlint-disable-next-line typescript/unbound-method
  const { signOut } = useAuth();
  const resetPostHog = usePostHogReset();
  const { activeThemeId, setActiveThemeId } = useThemeContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const pathname = location.pathname;
  const hideHeaderSearch = isMarketsMessagesTab(pathname, location.searchStr);

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);
  const handleProfile = () => {
    handleClose();
    runAsync(navigate({ to: "/settings/profile", search: {} }));
  };
  const darkModeActive = activeThemeId === "dark";
  const handleToggleDarkMode = () => {
    setActiveThemeId(darkModeActive ? "light" : "dark");
  };
  const handleSettings = () => {
    handleClose();
    runAsync(navigate({ to: "/settings", search: {} }));
  };
  const handleSystemStatus = () => {
    handleClose();
    runAsync(navigate({ to: "/settings/message-metrics", search: {} }));
  };
  const handleLogout = () => {
    handleClose();
    resetPostHog();
    runAsync(signOut());
  };
  const demoActive = isDemoMode();
  const handleToggleDemo = () => {
    handleClose();
    setDemoMode(!demoActive);
    // Full reload so every cached query refetches under (or out of) demo mode.
    globalThis.window?.location.assign("/markets");
  };

  const profileLabel = getProfileLabel(currentUser?.name);
  const headerLinks = compact ? [] : NAV_LINKS;

  return (
    <>
      <AppBar
        position={compact ? "fixed" : "static"}
        elevation={0}
        inert={headerHidden ? true : undefined}
        sx={(theme) => ({
          pointerEvents: headerHidden ? "none" : undefined,
          backgroundColor: theme.app?.headerBackground ?? "#0F172A",
          borderBottom: "1px solid #334155",
          boxShadow: "0px 1px 0px rgba(255, 255, 255, 0.08)",
          ...(compact
            ? {
                top: 0,
                left: 0,
                right: `${dockedOffset}px`,
                boxSizing: "border-box",
                paddingTop: "env(safe-area-inset-top, 0px)",
                height: `calc(${APP_HEADER_HEIGHT_PX}px + env(safe-area-inset-top, 0px))`,
                zIndex: theme.zIndex.appBar,
                transform: `translate3d(0, calc(-1 * ${chromeLeadingHide(APP_HEADER_HEIGHT_PX)}), 0)`,
                opacity: chromeLeadingOpacity(APP_HEADER_HEIGHT_PX),
                transition: theme.transitions.create("right", {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.enteringScreen,
                }),
                willChange: "transform, opacity, right",
              }
            : {}),
        })}
      >
        <Toolbar
          disableGutters
          data-app-header-toolbar=""
          sx={{
            gap: 1,
            padding: compact
              ? "0 max(12px, env(safe-area-inset-right, 0px)) 0 max(12px, env(safe-area-inset-left, 0px))"
              : "0 24px",
            minHeight: `${APP_HEADER_HEIGHT_PX}px !important`,
            height: APP_HEADER_HEIGHT_PX,
          }}
        >
          {/* Left: brand + nav */}
          <Box
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              // Keep nav at its size so a shrinking search cannot cover it.
              [theme.breakpoints.between(HEADER_COMPACT_BREAKPOINT_PX, "lg")]: {
                flex: "0 1 auto",
                overflow: "hidden",
              },
            })}
          >
            <HeaderBrand compact={compact} firmName={firm?.name} />

            <Box
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                gap: "4px",
                flexShrink: 0,
                "& > *": { m: 0 },
                [theme.breakpoints.up("lg")]: {
                  flexShrink: 1,
                },
              })}
            >
              {headerLinks.map((item) => (
                <NavItem
                  key={item.label}
                  Icon={item.Icon}
                  label={item.label}
                  to={item.to}
                  isActive={pathname.startsWith(item.to)}
                  singleLineOnLarge={item.singleLineOnLarge}
                />
              ))}
              {compact
                ? null
                : DISABLED_NAV_LINKS.map((item) => (
                    <NavItem key={item.label} Icon={item.Icon} label={item.label} disabled />
                  ))}
            </Box>
          </Box>

          <HeaderActions
            compact={compact}
            hideSearch={hideHeaderSearch}
            menuOpen={open}
            onAvatarClick={handleAvatarClick}
          />
          <AccountMenu
            anchorEl={anchorEl}
            open={open}
            compact={compact}
            profileLabel={profileLabel}
            firmName={firm?.name}
            email={currentUser?.email}
            isSystemAdmin={currentUser?.role === "SystemAdmin"}
            demoActive={demoActive}
            darkModeActive={darkModeActive}
            onClose={handleClose}
            onProfile={handleProfile}
            onSettings={handleSettings}
            onToggleDemo={handleToggleDemo}
            onToggleDarkMode={handleToggleDarkMode}
            onSystemStatus={handleSystemStatus}
            onLogout={handleLogout}
          />
        </Toolbar>
      </AppBar>
      {compact ? (
        <Box
          aria-hidden
          sx={{
            height: `calc(${chromeRemaining(APP_HEADER_HEIGHT_PX)} + env(safe-area-inset-top, 0px))`,
            overflow: "hidden",
            flexShrink: 0,
          }}
        />
      ) : null}
      {compact ? (
        <MobileBottomNav
          pathname={pathname}
          accountOpen={open}
          onAccountClick={handleAvatarClick}
        />
      ) : null}
    </>
  );
}
